import { useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import type { App, Environment, Project } from '@hangar/shared';
import { useCan } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { GatedButton } from '@/components/GatedButton';
import { Icon } from '@/components/Icon';
import { EnvPill, PlatformIcon, Tag } from '@/components/badges';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EnvVarSheet } from '@/features/env/EnvVarSheet';
import { ImportSheet } from '@/features/env/ImportSheet';
import type { ScopeSelection } from '@/features/env/ScopePicker';
import { StepCard, StepNote } from './StepCard';
import type { StepProps } from './StepShared';
import { envNote } from './stepNotesModel';
import { envScope, tileKeysView, tileStatus } from './stepViewModel';
import { STEP_TITLE, stepNumber } from './steps';
import { teamKeys, type EnvTile, type Setup } from './useSetup';

/** The key count of a tile: a skeleton while it loads, the number, or a dash when the resolve request failed. */
function TileKeys({ tile }: { tile: EnvTile }) {
  const view = tileKeysView(tile);
  const keys = teamKeys(tile.resolution) ?? 0;
  if (view === 'loading') return <Skeleton className="h-6 w-16" />;
  if (view === 'failed') {
    return (
      <span className="font-mono text-lg leading-6 text-subtle" title="The resolve request failed">
        —
      </span>
    );
  }
  return (
    <span className="tabular font-mono text-lg font-semibold leading-6 text-foreground" title="Keys your team set that reach this environment (Hangar's own HANGAR_* values not counted)">
      {keys} <span className="text-sm font-normal text-muted-foreground">{keys === 1 ? 'key' : 'keys'}</span>
    </span>
  );
}

/** One environment: how many keys resolve for the app there, on which platform, and how many required ones are missing. */
function EnvTileCard({ tile }: { tile: EnvTile }) {
  const missing = tile.resolution?.missing.length ?? 0;
  const status = tileStatus(tile, missing);
  return (
    <div data-testid={`gs-env-${tile.environment.slug}`} data-missing={missing} className={cn('flex min-w-0 flex-col items-center gap-1.5 rounded-lg border px-3 py-3 text-center', missing > 0 && 'border-awaiting/40 bg-awaiting/5')}>
      <EnvPill env={tile.environment} />
      <TileKeys tile={tile} />
      <span className="flex items-center gap-1 font-mono text-[11px] text-subtle">
        <PlatformIcon platform={tile.platform} size={13} />
        {'missing' in status ? <span className="text-awaiting">{status.missing} missing</span> : status.text}
      </span>
    </div>
  );
}

/** A required key without a value in an environment, with a shortcut to add it when the role may write. */
function MissingKeyNote({ missing, canWrite, onAdd }: { missing: { key: string; environment: string }; canWrite: boolean; onAdd: () => void }) {
  return (
    <StepNote
      tone="warning"
      testId={`gs-env-missing-${missing.environment}-${missing.key}`}
      action={
        canWrite ? (
          <Button size="sm" variant="outline" onClick={onAdd}>
            Add it
          </Button>
        ) : undefined
      }
    >
      <code className="font-mono text-xs font-semibold">{missing.key}</code> is required in <strong>{missing.environment}</strong> and missing.
    </StepNote>
  );
}

interface BodyProps {
  setup: Setup;
  canWrite: boolean;
  onAdd: (environment: string) => void;
}

/** One tile per environment, a warning per missing required key, and the note about public values. */
function EnvTiles({ setup, canWrite, onAdd }: BodyProps) {
  return (
    <>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(8.5rem,1fr))] gap-2.5" data-testid="gs-env-tiles">
        {setup.envTiles.map((t) => (
          <EnvTileCard key={t.environment.id} tile={t} />
        ))}
      </div>
      {setup.facts.envMissing.map((m) => (
        <MissingKeyNote key={`${m.environment}-${m.key}`} missing={m} canWrite={canWrite} onAdd={() => onAdd(m.environment)} />
      ))}
      <p className="text-xs text-subtle">
        <code className="font-mono">EXPO_PUBLIC_*</code> values end up in the app bundle, so they can never be secret; mark the ones <code className="font-mono">app.config.ts</code> reads as config time.
      </p>
    </>
  );
}

function EnvBody({ setup, canWrite, onAdd }: BodyProps) {
  if (!setup.app || !setup.project) return <StepNote tone="muted">Connect the app first (step {stepNumber('app')}).</StepNote>;
  if (!setup.canReadEnv.allowed) {
    return (
      <StepNote tone="muted" icon="visibility_off">
        Your role cannot read environment variables; a member or an admin sets them.
      </StepNote>
    );
  }
  return <EnvTiles setup={setup} canWrite={canWrite} onAdd={onAdd} />;
}

function envFootnote(project: Project | undefined, app: App | undefined): ReactNode {
  if (!project || !app) return null;
  return (
    <Link to="/projects/$slug/env" params={{ slug: project.slug }} search={{ app: app.slug }} data-testid="gs-open-env" className="inline-flex items-center gap-1 text-[13px] font-medium text-foreground underline-offset-4 hover:underline">
      Open Environment variables
      <Icon name="arrow_forward" size={16} />
    </Link>
  );
}

interface FooterProps {
  canWrite: ReturnType<typeof useCan>;
  current: boolean;
  onImport: () => void;
  onAdd: () => void;
}

function EnvFooter({ canWrite, current, onImport, onAdd }: FooterProps) {
  return (
    <>
      <GatedButton size="sm" variant="outline" data-testid="gs-env-import" can={canWrite} onClick={onImport}>
        <Icon name="upload" size={16} />
        Import .env
      </GatedButton>
      <GatedButton size="sm" variant={current ? 'default' : 'outline'} data-testid="gs-env-add" can={canWrite} onClick={onAdd}>
        <Icon name="add" size={16} />
        Add variable
      </GatedButton>
    </>
  );
}

interface SheetsProps {
  setup: Setup;
  project: Project;
  environments: Environment[];
  sheet: ScopeSelection | null;
  importing: boolean;
  onSheet: (next: ScopeSelection | null) => void;
  onImporting: (open: boolean) => void;
  scope: ScopeSelection;
}

/** The add-variable sheet and the .env import sheet, opened from the footer and from a missing key. */
function EnvSheets({ setup, project, environments, sheet, importing, onSheet, onImporting, scope }: SheetsProps) {
  return (
    <>
      <EnvVarSheet open={Boolean(sheet)} onOpenChange={(o) => (o ? undefined : onSheet(null))} projectId={project.id} scope={sheet ?? scope} apps={setup.apps.data} environments={environments} />
      <ImportSheet open={importing} onOpenChange={onImporting} projectId={project.id} scope={scope} apps={setup.apps.data} />
    </>
  );
}

/** Step 5 · Environment variables: the effective keys per environment, missing required keys, add and import in place. */
export function EnvStep({ setup, index }: StepProps) {
  const { project, app, environments } = setup;
  const canWrite = useCan('env:write', project?.id);
  const [sheet, setSheet] = useState<ScopeSelection | null>(null);
  const [importing, setImporting] = useState(false);
  const current = setup.progress.next === 'env';
  const noAccess = !setup.canReadEnv.allowed;
  const scope = (environment?: string) => envScope(app, environments, environment);
  const footer = app && project && !noAccess ? <EnvFooter canWrite={canWrite} current={current} onImport={() => setImporting(true)} onAdd={() => setSheet(scope())} /> : null;
  return (
    <>
      <StepCard
        state={setup.state('env')}
        current={current}
        index={index}
        title={STEP_TITLE.env}
        summary="Encrypted, versioned values; every build freezes a snapshot of them. Required keys missing in an environment block its builds."
        pill={noAccess ? <Tag>No access</Tag> : undefined}
        collapsedNote={envNote(setup.facts.envMissing.length, setup.facts.envKeys)}
        footnote={envFootnote(project, app)}
        footer={footer}
      >
        <EnvBody setup={setup} canWrite={canWrite.allowed} onAdd={(environment) => setSheet(scope(environment))} />
      </StepCard>
      {project ? <EnvSheets setup={setup} project={project} environments={environments} sheet={sheet} importing={importing} onSheet={setSheet} onImporting={setImporting} scope={scope()} /> : null}
    </>
  );
}
